import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { CustomLoading } from "components/CustomLoading";
import { FileText, HelpCircle, Link2, FileType2, FolderClosed, Pencil, Trash2 } from "lucide-react";
import ConfirmModal from "./ConfirmModal";
import { User, loadUsers, displayName, initials, avatarColor } from "./user-display";
import { LucideIcon } from "./icon-registry";
import { formatRelative } from "./category-rows";

interface Category {
    id: string;
    name: string;
    color: string | null;
    icon: string | null;
}

interface DraftPage {
    id: string;
    title: string;
    categoryId: string;
    folderId: string | null;
    status: string;
    updatedAt: string;
    createdAt: string;
    // set when this row is a pending draft held on an already-published page
    hasDraft?: boolean;
    draftBody?: string | null;
    draftTitle?: string | null;
    draftUpdatedAt?: string | null;
}

interface ProfileUser {
    id: string;
    name: string;
    email: string | null;
    role: string | null;
    isSystemAdministrator: boolean;
    isActive: boolean;
}

interface OwnedPage {
    id: string;
    categoryId: string;
    title: string;
    status: string;
    updatedAt: string;
}

interface OwnedCategory {
    id: string;
    name: string;
    color: string | null;
    icon: string | null;
    accessLevel: string;
    updatedAt: string;
}

interface AnsweredQuestion {
    id: string;
    title: string;
    categoryId: string | null;
    answeredAt: string;
}

interface CompletedRequest {
    id: string;
    pageId: string | null;
    categoryId: string | null;
    proposalType: string;
    requestedTitle: string | null;
    pageTitle: string | null;
    status: string;
    reviewedAt: string;
    updatedAt: string;
}

interface ProfileLink {
    id: string;
    title: string;
    url: string;
    categoryId: string;
    updatedAt: string;
}

interface ProfileDocument {
    id: string;
    filename: string;
    categoryId: string;
    updatedAt: string;
}

interface ProfileResponse {
    user: ProfileUser;
    ownedPages: OwnedPage[];
    ownedCategories: OwnedCategory[];
    answeredQuestions: AnsweredQuestion[];
    completedRequests: CompletedRequest[];
    links: ProfileLink[];
    documents: ProfileDocument[];
}

type TabKey = "drafts" | "ownedPages" | "ownedCategories" | "answered" | "completed" | "links" | "documents";

interface Props {
    currentUserId: string | null;
    viewUserId: string | null;
    categories: Category[];
    onSelectPage: (pageId: string, categoryId: string) => void;
    onSelectCategory: (categoryId: string) => void;
    onEditPage: (page: DraftPage) => void;
}

export default function ProfileView({
    currentUserId,
    viewUserId,
    categories,
    onSelectPage,
    onSelectCategory,
    onEditPage,
}: Props) {
    const targetId = viewUserId || currentUserId;
    const isSelf = !!targetId && targetId === currentUserId;

    const [loading, setLoading] = useState(true);
    const [profile, setProfile] = useState<ProfileResponse | null>(null);
    const [drafts, setDrafts] = useState<DraftPage[]>([]);
    const [usersMap, setUsersMap] = useState<Map<string, User>>(new Map());
    const [tab, setTab] = useState<TabKey>(isSelf ? "drafts" : "ownedPages");
    const [deleteTarget, setDeleteTarget] = useState<DraftPage | null>(null);

    useEffect(() => {
        if (!targetId) return;
        setLoading(true);
        const promises: Promise<any>[] = [
            axios.get(`/api/manuals/users/${targetId}/profile`),
            loadUsers(),
        ];
        if (isSelf) promises.push(axios.get("/api/manuals/pages/drafts"));

        Promise.all(promises)
            .then(results => {
                const profileRes = results[0];
                const userList: User[] = results[1] || [];
                setUsersMap(new Map(userList.map(u => [u.id, u])));
                if (profileRes.data.success) {
                    setProfile(profileRes.data as ProfileResponse);
                } else {
                    toast.error(profileRes.data.message || "Failed to load profile");
                }
                if (isSelf) {
                    const draftsRes = results[2];
                    if (draftsRes?.data?.success) setDrafts(draftsRes.data.items);
                }
            })
            .catch(() => toast.error("Failed to load profile"))
            .finally(() => setLoading(false));
    }, [targetId, isSelf]);

    const categoriesById = useMemo(() => new Map(categories.map(c => [c.id, c])), [categories]);

    const handleDeleteDraft = () => {
        if (!deleteTarget) return;
        const id = deleteTarget.id;
        const fromPublished = deleteTarget.status === "published";
        setDeleteTarget(null);
        // A published-page draft is discarded (live page kept); a never-published
        // draft is the page itself, so it's deleted.
        const request = fromPublished
            ? axios.post(`/api/manuals/pages/${id}/discard-draft`)
            : axios.delete(`/api/manuals/pages/${id}`);
        request
            .then(res => {
                if (res.data.success) {
                    toast.success(fromPublished ? "Draft changes discarded" : "Draft deleted");
                    setDrafts(prev => prev.filter(d => d.id !== id));
                } else {
                    toast.error(res.data.message || (fromPublished ? "Failed to discard" : "Failed to delete"));
                }
            })
            .catch(() => toast.error(fromPublished ? "Failed to discard" : "Failed to delete"));
    };

    if (!targetId) return null;

    const draftsCount = isSelf ? drafts.length : 0;
    const counts = profile ? {
        drafts: draftsCount,
        ownedPages: profile.ownedPages.length,
        ownedCategories: profile.ownedCategories.length,
        answered: profile.answeredQuestions.length,
        completed: profile.completedRequests.length,
        links: profile.links.length,
        documents: profile.documents.length,
    } : { drafts: 0, ownedPages: 0, ownedCategories: 0, answered: 0, completed: 0, links: 0, documents: 0 };

    const tabs: { key: TabKey; label: string; count: number; hidden?: boolean }[] = [
        { key: "drafts", label: "Drafts & To-dos", count: counts.drafts, hidden: !isSelf },
        { key: "ownedPages", label: "Owned pages", count: counts.ownedPages },
        { key: "ownedCategories", label: "Owned categories", count: counts.ownedCategories },
        { key: "answered", label: "Answered questions", count: counts.answered },
        { key: "completed", label: "Completed requests", count: counts.completed },
        { key: "links", label: "Links", count: counts.links },
        { key: "documents", label: "Documents", count: counts.documents },
    ];

    return (
        <div className="m-cat-page">
            {loading || !profile ? (
                <div className="m-loading"><CustomLoading /></div>
            ) : (
                <>
                    <div className="m-profile-hero">
                        <span
                            className="m-profile-avatar"
                            style={{ background: avatarColor(profile.user.id) }}
                        >
                            {initials(profileDisplayName(profile.user))}
                        </span>
                        <div className="m-profile-hero-main">
                            <div className="m-profile-hero-line">
                                <span className="m-profile-name">{profileDisplayName(profile.user)}</span>
                                {profile.user.isSystemAdministrator && (
                                    <span className="m-profile-role-pill">admin</span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="m-profile-tabs">
                        {tabs.filter(t => !t.hidden).map(t => (
                            <button
                                key={t.key}
                                className={`m-cat-tab${tab === t.key ? " active" : ""}`}
                                onClick={() => setTab(t.key)}
                            >
                                <span>{t.label}</span>
                                <span className="m-cat-tab-count">{t.count}</span>
                            </button>
                        ))}
                    </div>

                    <div className="m-profile-body">
                        {tab === "drafts" && (
                            <ProfileTable
                                rows={drafts.map(d => {
                                    const fromPublished = d.status === "published";
                                    return {
                                        key: d.id,
                                        icon: <FileText size={18} className="icon" color="#1e85e5" />,
                                        title: (
                                            <>
                                                {d.title || "Untitled"}
                                                {fromPublished && (
                                                    <span className="m-content-row-badge draft m-draft-source-badge" title="Unpublished changes to a live page. The published page stays as-is until you publish these.">
                                                        published-page draft
                                                    </span>
                                                )}
                                            </>
                                        ),
                                        type: "Draft",
                                        meta: `Updated ${formatRelative(fromPublished ? (d.draftUpdatedAt || d.updatedAt) : d.updatedAt)}`,
                                        onClick: () => onSelectPage(d.id, d.categoryId),
                                        actions: (
                                            <>
                                                <button className="m-topbar-btn" onClick={e => { e.stopPropagation(); onEditPage(d as any); }} title={fromPublished ? "Edit draft changes" : "Edit draft"}>
                                                    <Pencil size={14} />
                                                </button>
                                                <button className="m-topbar-btn m-topbar-btn-danger" onClick={e => { e.stopPropagation(); setDeleteTarget(d); }} title={fromPublished ? "Discard draft changes" : "Delete draft"}>
                                                    <Trash2 size={14} />
                                                </button>
                                            </>
                                        ),
                                    };
                                })}
                                empty="No drafts yet. New pages you create — and unpublished changes to live pages — will land here until you publish them."
                            />
                        )}
                        {tab === "ownedPages" && (
                            <ProfileTable
                                rows={profile.ownedPages.map(p => {
                                    const cat = categoriesById.get(p.categoryId);
                                    return {
                                        key: p.id,
                                        icon: <FileText size={18} className="icon" color="#1e85e5" />,
                                        title: p.title || "Untitled",
                                        type: "Page",
                                        meta: cat ? `${cat.name} · Updated ${formatRelative(p.updatedAt)}` : `Updated ${formatRelative(p.updatedAt)}`,
                                        onClick: () => onSelectPage(p.id, p.categoryId),
                                    };
                                })}
                                empty={isSelf ? "You don't own any published pages yet." : "No pages owned."}
                            />
                        )}
                        {tab === "ownedCategories" && (
                            <ProfileTable
                                rows={profile.ownedCategories.map(c => ({
                                    key: c.id,
                                    icon: (
                                        <span className="m-row-folder-swatch" style={{ backgroundColor: c.color || "#94a3b8" }}>
                                            <LucideIcon name={c.icon || "FolderClosed"} size={14} color="#fff" />
                                        </span>
                                    ),
                                    title: c.name,
                                    type: c.accessLevel === "restricted" ? "Restricted" : "Category",
                                    meta: `Updated ${formatRelative(c.updatedAt)}`,
                                    onClick: () => onSelectCategory(c.id),
                                }))}
                                empty={isSelf ? "You don't own any categories." : "No categories owned."}
                            />
                        )}
                        {tab === "answered" && (
                            <ProfileTable
                                rows={profile.answeredQuestions.map(q => ({
                                    key: q.id,
                                    icon: <HelpCircle size={18} className="icon" color="#10b981" />,
                                    title: q.title,
                                    type: "Question",
                                    meta: `Answered ${formatRelative(q.answeredAt)}`,
                                }))}
                                empty={isSelf ? "You haven't answered any questions yet." : "No answered questions."}
                            />
                        )}
                        {tab === "completed" && (
                            <ProfileTable
                                rows={profile.completedRequests.map(r => ({
                                    key: r.id,
                                    icon: <FileText size={18} className="icon" color="#94a3b8" />,
                                    title: r.pageTitle || r.requestedTitle || "(request)",
                                    type: proposalTypeLabel(r.proposalType),
                                    meta: `Reviewed ${formatRelative(r.reviewedAt || r.updatedAt)}`,
                                    onClick: r.pageId ? () => onSelectPage(r.pageId!, r.categoryId || "") : undefined,
                                }))}
                                empty={isSelf ? "No completed requests yet." : "No completed requests."}
                            />
                        )}
                        {tab === "links" && (
                            <ProfileTable
                                rows={profile.links.map(l => {
                                    const cat = categoriesById.get(l.categoryId);
                                    return {
                                        key: l.id,
                                        icon: <Link2 size={18} className="icon" color="#1e85e5" />,
                                        title: l.title,
                                        type: "Link",
                                        meta: cat ? `${cat.name} · Updated ${formatRelative(l.updatedAt)}` : `Updated ${formatRelative(l.updatedAt)}`,
                                        onClick: () => window.open(l.url, "_blank", "noopener,noreferrer"),
                                    };
                                })}
                                empty={isSelf ? "You haven't added any links yet." : "No links added."}
                            />
                        )}
                        {tab === "documents" && (
                            <ProfileTable
                                rows={profile.documents.map(d => {
                                    const cat = categoriesById.get(d.categoryId);
                                    return {
                                        key: d.id,
                                        icon: <FileType2 size={18} className="icon" color="#64748b" />,
                                        title: d.filename,
                                        type: "Document",
                                        meta: cat ? `${cat.name} · Updated ${formatRelative(d.updatedAt)}` : `Updated ${formatRelative(d.updatedAt)}`,
                                        onClick: () => window.location.assign(`/api/manuals/documents/${d.id}/download`),
                                    };
                                })}
                                empty={isSelf ? "You haven't uploaded any documents yet." : "No documents uploaded."}
                            />
                        )}
                    </div>
                </>
            )}

            {deleteTarget && (
                <ConfirmModal
                    title={deleteTarget.status === "published" ? "Discard draft changes" : "Delete draft"}
                    message={deleteTarget.status === "published"
                        ? `Discard your unpublished changes to "${deleteTarget.title || "Untitled"}"? The published page stays exactly as it is now.`
                        : `Delete "${deleteTarget.title || "Untitled"}"? This cannot be undone.`}
                    confirmLabel={deleteTarget.status === "published" ? "Discard" : "Delete"}
                    danger
                    onConfirm={handleDeleteDraft}
                    onCancel={() => setDeleteTarget(null)}
                />
            )}
        </div>
    );
}

function profileDisplayName(u: ProfileUser): string {
    const fakeUser: User = {
        id: u.id,
        name: u.name,
        email: u.email,
        role: u.role,
        isSystemAdministrator: u.isSystemAdministrator,
    };
    return displayName(fakeUser);
}

function proposalTypeLabel(t: string): string {
    if (t === "edit") return "Edit request";
    if (t === "update_request") return "Update request";
    if (t === "new_page_request") return "New page request";
    return "Request";
}

interface RowDef {
    key: string;
    icon: React.ReactNode;
    title: React.ReactNode;
    type: string;
    meta?: string;
    onClick?: () => void;
    actions?: React.ReactNode;
}

function ProfileTable({ rows, empty }: { rows: RowDef[]; empty: string }) {
    if (rows.length === 0) {
        return <div className="m-profile-empty">{empty}</div>;
    }
    return (
        <div className="m-profile-table">
            <div className="m-profile-table-head">
                <span>Name</span>
                <span>Type</span>
            </div>
            {rows.map(r => (
                <div
                    key={r.key}
                    className={`m-profile-table-row${r.onClick ? " clickable" : ""}`}
                    onClick={r.onClick}
                    role={r.onClick ? "button" : undefined}
                >
                    <div className="m-profile-row-main">
                        <span className="m-profile-row-icon">{r.icon}</span>
                        <div className="m-profile-row-text">
                            <div className="m-profile-row-title">{r.title}</div>
                            {r.meta && <div className="m-profile-row-meta">{r.meta}</div>}
                        </div>
                    </div>
                    <div className="m-profile-row-type">{r.type}</div>
                    {r.actions && <div className="m-profile-row-actions">{r.actions}</div>}
                </div>
            ))}
        </div>
    );
}
